import React, { useMemo, useState } from "react";
import { Star, X } from "lucide-react";
import { apiFetch } from "../api";
import { toast } from "react-hot-toast";

const MAX_COMMENT_LENGTH = 2000;

export default function ServiceRatingForm({ planUuid, onClose, onSubmitted, compact = false }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(() => {
    const trimmed = comment.trim();
    return rating > 0 && trimmed.length > 0 && trimmed.length <= MAX_COMMENT_LENGTH;
  }, [rating, comment]);

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        rating,
        comment: comment.trim(),
        planUuid: planUuid || undefined,
      };
      const res = await apiFetch("/ratings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let message = "Не удалось отправить отзыв";
        try {
          const data = await res.json();
          message = data?.error || message;
        } catch (_) {}
        setError(message);
        setSubmitting(false);
        return;
      }
      toast.success("Спасибо за отзыв!");
      setSubmitting(false);
      setRating(0);
      setComment("");
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      console.error("[service rating]", err);
      setError("Не удалось отправить отзыв");
      setSubmitting(false);
    }
  };

  const disabledHint = !canSubmit ? "Выберите оценку и введите комментарий" : "";

  return (
    <div className={`relative ${compact ? "" : "rounded-xl border bg-white p-4 shadow-sm"}`}>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Закрыть блок с оценкой"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <div className={`${compact ? "" : "pr-6"} space-y-3`}>
        <div>
          <div className="text-base font-semibold text-slate-900">Оценка сервиса</div>
          <p className="text-sm text-slate-600">Напишите пару строк, чтобы сервис стал удобнее</p>
        </div>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((value) => {
            const active = value <= rating;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setRating(value)}
                className="p-1"
                aria-label={`Поставить ${value} из 5`}
              >
                <Star
                  className={`h-6 w-6 ${active ? "fill-amber-400 text-amber-500" : "text-slate-300"}`}
                />
              </button>
            );
          })}
        </div>
        <div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
            maxLength={MAX_COMMENT_LENGTH}
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
            placeholder="Что можно улучшить?"
          />
          <div className="mt-1 text-right text-xs text-slate-500">
            {comment.length}/{MAX_COMMENT_LENGTH}
          </div>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit || submitting}
          title={disabledHint}
          className="inline-flex h-10 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Отправляем..." : "Отправить"}
        </button>
      </div>
    </div>
  );
}
